import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { IconNotification } from "./notifications";

/**
 * Toast systém postavený nad nativním Untitled UI `IconNotification`
 * (`@/components/application/notifications`).
 *
 * Zachovává PŘESNĚ legacy API `useToast()` / `ToastProvider` z původního
 * `components/ui/toast.tsx`, aby consumeři (push/dismiss) nebylo nutné měnit.
 * Vizuál a tokeny přebírá od nativní notifikace; tady jen řešíme
 * životní cyklus (push/auto-dismiss) + aria-live region.
 */

export interface Toast {
  id: string;
  variant?: "info" | "success" | "warning" | "error";
  message: ReactNode;
  durationMs?: number;
}

interface ToastContextValue {
  push: (toast: Omit<Toast, "id">) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be wrapped in <ToastProvider>");
  return ctx;
}

// Legacy variant → nativní `IconNotification` color.
const variantToColor: Record<
  NonNullable<Toast["variant"]>,
  "brand" | "success" | "warning" | "error"
> = {
  info: "brand",
  success: "success",
  warning: "warning",
  error: "error",
};

// Krátký titulek pro každou variantu — nativní IconNotification má title
// (tučný) + description; legacy toast nese jen jednu zprávu, tu mapujeme
// na description a doplníme srozumitelný český titulek.
const variantToTitle: Record<NonNullable<Toast["variant"]>, string> = {
  info: "Informace",
  success: "Hotovo",
  warning: "Upozornění",
  error: "Chyba",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idCounter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (t: Omit<Toast, "id">) => {
      idCounter.current += 1;
      const id = `toast-${idCounter.current}`;
      const duration = t.durationMs ?? 5000;
      setToasts((cur) => [...cur, { ...t, id }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="region"
        aria-label="Notifikace"
        aria-live="polite"
        className="fixed right-6 bottom-6 z-[110] flex max-w-sm flex-col gap-3"
      >
        {toasts.map((t) => {
          const variant = t.variant ?? "info";
          return (
            <div
              key={t.id}
              data-variant={variant}
              role={variant === "error" ? "alert" : "status"}
            >
              <IconNotification
                color={variantToColor[variant]}
                title={variantToTitle[variant]}
                // Legacy `message` může být ReactNode; nativní `description`
                // je typovaný jako string, ale React vykreslí ReactNode bez
                // problému — proto cast, abychom zachovali stávající API.
                description={t.message as unknown as string}
                hideDismissLabel
                onClose={() => dismiss(t.id)}
              />
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
